/**
 * WebSocket Context for Real-Time Task Updates
 * Provides WebSocket connection management with authentication, reconnection, and event handling
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useStatus } from './StatusContext';

// WebSocket connection states
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'authenticating' | 'authenticated' | 'error';

// WebSocket message types
export interface WebSocketMessage {
  type: 'notification' | 'response' | 'error' | 'ping' | 'pong';
  action?: string;
  topic?: string;
  data?: any;
  requestId?: string;
  timestamp?: string;
  error?: string;
}

// Task event types
export interface TaskEvent {
  event_type: 'task_created' | 'task_updated' | 'task_deleted' | 'task_assigned';
  task_id: string;
  family_id: string;
  user_id?: string;
  data?: any;
  timestamp: string;
}

interface WebSocketContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  isAuthenticated: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: (topic: string, callback: (data: any) => void) => () => void;
  sendMessage: (action: string, payload: any) => Promise<any>;
  on: (event: string, callback: (data: any) => void) => () => void;
  lastError: string | null;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

interface WebSocketProviderProps {
  children: React.ReactNode;
  url?: string;
  autoConnect?: boolean;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
  autoConnect = true
}) => {
  const { idToken, isAuthenticated: userAuthenticated, user_id, family_id } = useStatus();

  // WebSocket state
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 2000;

  // Subscriptions and event listeners
  const subscriptionsRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
  const eventListenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
  const requestCallbacksRef = useRef<Map<string, { resolve: (value: any) => void; reject: (error: any) => void }>>(new Map());

  // WebSocket URL - check for environment variable or use provided URL
  const wsUrl = url || process.env.REACT_APP_WEBSOCKET_URL || 'wss://6nt8vtyvge.execute-api.us-east-2.amazonaws.com/dev';

  // Cleanup function
  const cleanup = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Send heartbeat/ping messages
  const startHeartbeat = useCallback(() => {
    cleanup();
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          action: 'ping',
          timestamp: new Date().toISOString()
        }));
      }
    }, 30000); // Send ping every 30 seconds
  }, [cleanup]);

  // Emit event to listeners
  const emit = useCallback((event: string, data?: any) => {
    const listeners = eventListenersRef.current.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      console.log('WebSocket message received:', message);

      // Handle ping/pong
      if (message.type === 'ping') {
        wsRef.current?.send(JSON.stringify({ action: 'pong', timestamp: new Date().toISOString() }));
        return;
      }

      // Handle response to request
      if (message.requestId && requestCallbacksRef.current.has(message.requestId)) {
        const callback = requestCallbacksRef.current.get(message.requestId);
        requestCallbacksRef.current.delete(message.requestId);

        if (message.type === 'error' || message.error) {
          callback?.reject(new Error(message.error || 'Request failed'));
        } else {
          callback?.resolve(message.data || message);
        }
        return;
      }

      // Handle notifications (topic-based)
      if (message.type === 'notification' && message.topic) {
        const topicListeners = subscriptionsRef.current.get(message.topic);
        if (topicListeners) {
          topicListeners.forEach(callback => {
            try {
              callback(message.data);
            } catch (error) {
              console.error(`Error in subscription callback for ${message.topic}:`, error);
            }
          });
        }

        // Emit event for task updates
        if (message.topic.startsWith('task:') || message.topic.startsWith('family:')) {
          emit('taskUpdate', message.data);
        }
      }

      // Emit general message event
      emit('message', message);
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  }, [emit]);

  // Authenticate WebSocket connection
  const authenticate = useCallback(async () => {
    if (!idToken || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    setConnectionState('authenticating');

    try {
      const response = await sendMessageInternal('authorize', {
        token: idToken,
        userId: user_id,
        familyId: family_id
      });

      setConnectionState('authenticated');
      console.log('WebSocket authenticated:', response);

      // Auto-subscribe to user and family topics
      if (user_id) {
        subscriptionsRef.current.set(`user:${user_id}`, new Set());
        sendMessageInternal('subscribe', { topic: `user:${user_id}` }).catch(console.error);
      }
      if (family_id) {
        subscriptionsRef.current.set(`family:${family_id}`, new Set());
        sendMessageInternal('subscribe', { topic: `family:${family_id}` }).catch(console.error);
      }

      emit('authenticated', response);
      startHeartbeat();
    } catch (error) {
      console.error('WebSocket authentication failed:', error);
      setConnectionState('error');
      setLastError('Authentication failed');
    }
  }, [idToken, user_id, family_id, emit, startHeartbeat]);

  // Internal send message function
  const sendMessageInternal = (action: string, payload: any = {}): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      requestCallbacksRef.current.set(requestId, { resolve, reject });

      wsRef.current.send(JSON.stringify({
        action,
        payload,
        requestId,
        timestamp: new Date().toISOString()
      }));

      // Timeout after 30 seconds
      setTimeout(() => {
        if (requestCallbacksRef.current.has(requestId)) {
          requestCallbacksRef.current.delete(requestId);
          reject(new Error('Request timeout'));
        }
      }, 30000);
    });
  };

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Check if WebSocket is enabled via environment variable (beta launch: disabled)
    const wsEnabled = process.env.REACT_APP_WEBSOCKET_ENABLED !== 'false';
    if (!wsEnabled) {
      console.log('WebSocket disabled for beta launch. Real-time updates will be available in next release.');
      setConnectionState('disconnected');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Check if URL is placeholder or empty
    if (!wsUrl || wsUrl.includes('placeholder')) {
      console.warn('WebSocket URL not configured. Real-time features disabled.');
      setConnectionState('disconnected');
      setLastError('WebSocket endpoint not configured');
      return;
    }

    setConnectionState('connecting');
    setLastError(null);

    try {
      // Include auth token in WebSocket URL for API Gateway authentication
      let connectionUrl = wsUrl;
      if (idToken && userAuthenticated) {
        const separator = wsUrl.includes('?') ? '&' : '?';
        connectionUrl = `${wsUrl}${separator}token=${encodeURIComponent(idToken)}`;
        console.log('Connecting to WebSocket with authentication');
      } else {
        console.log('Connecting to WebSocket:', wsUrl);
      }

      const ws = new WebSocket(connectionUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
        emit('connected');

        // Authenticate if we have a token
        if (idToken && userAuthenticated) {
          authenticate();
        }
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionState('error');
        setLastError('Connection error');
        emit('error', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setConnectionState('disconnected');
        emit('disconnected', { code: event.code, reason: event.reason });
        cleanup();

        // Auto-reconnect if not intentional close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (userAuthenticated) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setLastError('Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setConnectionState('error');
      setLastError('Failed to create connection');
    }
  }, [wsUrl, idToken, userAuthenticated, authenticate, handleMessage, emit, cleanup]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    cleanup();
    if (wsRef.current) {
      wsRef.current.close(1000, 'Normal closure');
      wsRef.current = null;
    }
    setConnectionState('disconnected');
    subscriptionsRef.current.clear();
    eventListenersRef.current.clear();
    requestCallbacksRef.current.clear();
  }, [cleanup]);

  // Subscribe to topic
  const subscribe = useCallback((topic: string, callback: (data: any) => void): (() => void) => {
    if (!subscriptionsRef.current.has(topic)) {
      subscriptionsRef.current.set(topic, new Set());

      // Send subscribe request to server if connected
      if (connectionState === 'authenticated') {
        sendMessageInternal('subscribe', { topic }).catch(console.error);
      }
    }

    subscriptionsRef.current.get(topic)?.add(callback);

    // Return unsubscribe function
    return () => {
      const topicListeners = subscriptionsRef.current.get(topic);
      if (topicListeners) {
        topicListeners.delete(callback);

        if (topicListeners.size === 0) {
          subscriptionsRef.current.delete(topic);
          if (connectionState === 'authenticated') {
            sendMessageInternal('unsubscribe', { topic }).catch(console.error);
          }
        }
      }
    };
  }, [connectionState]);

  // Add event listener
  const on = useCallback((event: string, callback: (data: any) => void): (() => void) => {
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, new Set());
    }
    eventListenersRef.current.get(event)?.add(callback);

    // Return remove listener function
    return () => {
      const eventCallbacks = eventListenersRef.current.get(event);
      if (eventCallbacks) {
        eventCallbacks.delete(callback);
      }
    };
  }, []);

  // Send message
  const sendMessage = useCallback((action: string, payload: any): Promise<any> => {
    return sendMessageInternal(action, payload);
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    if (autoConnect && userAuthenticated && idToken && connectionState === 'disconnected') {
      connect();
    }
  }, [autoConnect, userAuthenticated, idToken, connectionState, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const value: WebSocketContextValue = {
    connectionState,
    isConnected: connectionState === 'connected' || connectionState === 'authenticating' || connectionState === 'authenticated',
    isAuthenticated: connectionState === 'authenticated',
    connect,
    disconnect,
    subscribe,
    sendMessage,
    on,
    lastError
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
